import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, money, formatApiErrorDetail } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Sparkles, QrCode, Heart, BellRing, Layers, Plus, Check } from "lucide-react";
import { ShareButton } from "@/components/ShareButton";

const SET_DISCOUNT = 0.2;
const SET_ID = "HOPE_CARDS_SET";

export default function HopeCards() {
  const { items, addItem } = useCart();
  const [cards, setCards] = useState([]);
  const [live, setLive] = useState(false);
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/hope-cards").then((r) => setCards(r.data)).catch(() => {});
    api.get("/settings").then((r) => setLive(!!r.data.hope_cards_live)).catch(() => {});
  }, []);

  const individualTotal = cards.reduce((s, c) => s + Number(c.price || 0), 0);
  const setPrice = Math.round(individualTotal * (1 - SET_DISCOUNT) * 100) / 100;
  const inCart = (id) => items.some((i) => i.id === id);
  const setInCart = inCart(SET_ID);

  const addCard = (c) => { addItem(c, 1); toast.success(`${c.name} added 💛`); };
  const addSet = () => {
    addItem({ id: SET_ID, name: "The Collector's Set", price: setPrice, image: cards[0]?.image || "", slug: "collectors-set" }, 1);
    toast.success("The Collector's Set added — you saved 20%! 💛");
  };

  const notify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/hope-cards/notify", { email, message: msg });
      toast.success("You're on the list — we'll let you know when they launch 💛");
      setEmail(""); setMsg("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="program-hope-cards">
      {/* Hero */}
      <section className="bg-[#F2EFEB]">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-20 text-center">
          <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase font-semibold text-[#8E9A86] bg-white border border-[#D9CBA0]/60 rounded-full px-4 py-1.5" data-testid="hope-cards-status">
            <Sparkles className="h-3.5 w-3.5" /> {live ? "Collectible Series · Now Available" : "Collectible Series · Coming Soon"}
          </span>
          <p className="font-heading text-xl sm:text-2xl text-[#8E9A86] tracking-wide">Hope &amp; Favor&trade;</p>
          <h1 className="font-heading text-5xl sm:text-6xl text-[#3A3632] mt-1 leading-tight">Keepsake Hope Cards</h1>
          <p className="font-hand text-3xl text-[#C4A4A4] mt-3">Encouragement worth keeping — and collecting.</p>
          <div className="gh-hairline my-7 max-w-[120px] mx-auto" />
          <p className="text-lg text-[#6B655F] leading-relaxed font-light">
            Hope &amp; Favor&trade; Keepsake Hope Cards aren't tossed after a day — they're tucked into drawers, taped to mirrors, and rediscovered on hard days. Each keepsake-quality design carries a gentle message and a QR code to a welcome video.{live ? " Collect the ones that speak to you, or save 20% with the full set." : " The collectible series is on its way — join the list to be the first to know."}
          </p>
          {live && (
            <button onClick={addSet} disabled={setInCart} data-testid="hero-add-set" className="btn-primary mt-8 inline-flex disabled:opacity-60">
              {setInCart ? <><Check className="h-4 w-4" /> Set in cart</> : <><Layers className="h-4 w-4" /> Get the Collector's Set · {money(setPrice)}</>}
            </button>
          )}
        </div>
      </section>

      {/* Quality showcase — example card with removable keepsake */}
      <section className="bg-white" data-testid="hope-card-showcase">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20 text-center">
          <span className="gh-overline">More than a card</span>
          <h2 className="font-heading text-3xl sm:text-4xl text-[#3A3632] mt-3">A keepsake to treasure</h2>
          <p className="font-hand text-2xl sm:text-3xl text-[#C4A4A4] mt-5 max-w-2xl mx-auto leading-snug">
            Every Hope Card is designed to remind someone they are loved, seen, and never alone.
          </p>
          <div className="gh-hairline my-8 max-w-[120px] mx-auto" />
          <figure className="rounded-2xl overflow-hidden border border-[#D9CBA0]/50 shadow-sm bg-[#FBF7F0] max-w-xl mx-auto">
            <img
              src="https://customer-assets.emergentagent.com/job_hope-boxes/artifacts/1jsrfp9m_GIFT%20-%20H%26F%20HOPE%20CARD.png"
              alt="Hope & Favor Hope Card shown open with an encouraging message, Bible verse, QR code, and a removable golden tree-of-life heart keepsake charm — usable as an ornament, keychain, purse charm, or daily reminder."
              className="w-full h-auto"
              loading="lazy"
            />
          </figure>
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 mt-8 text-sm text-[#6B655F]">
            <span className="inline-flex items-center gap-2"><Heart className="h-4 w-4 text-[#C4A4A4]" /> Removable keepsake charm</span>
            <span className="inline-flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#8E9A86]" /> Inside Bible verse</span>
            <span className="inline-flex items-center gap-2"><QrCode className="h-4 w-4 text-[#8E9A86]" /> QR code to a special message</span>
          </div>
        </div>
      </section>

      {/* Card designs */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="text-center mb-12">
          <span className="gh-overline">{live ? "The collection" : "A first look"}</span>
          <h2 className="font-heading text-3xl sm:text-4xl text-[#3A3632] mt-3">Designs in the series</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {cards.map((c, i) => (
            <div key={c.id} data-testid={`hope-card-${c.slug}`} className="bg-white rounded-xl border border-[#E5DFD5] overflow-hidden gh-fade-up flex flex-col" style={{ animationDelay: `${i * 60}ms` }}>
              <div className="relative aspect-[4/3] bg-[#F2EFEB] overflow-hidden">
                <img src={c.image} alt={c.name} className="w-full h-full object-cover" />
                {!live && <span className="absolute top-3 left-3 text-[10px] tracking-wide uppercase font-semibold text-white bg-[#3A3632]/80 rounded-full px-2.5 py-1">Coming soon</span>}
              </div>
              <div className="p-5 flex flex-col flex-1">
                <h3 className="font-heading text-xl text-[#3A3632]">{c.name}</h3>
                <p className="text-sm text-[#6B655F] mt-1 leading-relaxed line-clamp-2">{c.short_desc}</p>
                {c.reflection && <p className="font-hand text-lg text-[#8E9A86] mt-2 leading-tight">"{c.reflection}"</p>}
                <div className="mt-auto pt-4">
                  <div className="flex items-center justify-between">
                    <span className="text-[#8E9A86] font-semibold">{money(c.price)} <span className="text-xs text-[#8A8175] font-normal">each</span></span>
                    {live && (
                      <button onClick={() => addCard(c)} disabled={inCart(c.id)} data-testid={`hope-card-add-${c.slug}`}
                        className="bg-[#C4A4A4] text-white rounded-full px-4 py-2 text-sm font-medium hover:bg-[#B09090] transition-colors inline-flex items-center gap-1.5 disabled:opacity-60">
                        {inCart(c.id) ? <><Check className="h-4 w-4" /> Added</> : <><Plus className="h-4 w-4" /> Add</>}
                      </button>
                    )}
                  </div>
                  {live && !setInCart && (
                    <button onClick={addSet} data-testid={`hope-card-complete-set-${c.slug}`} className="mt-2 text-xs text-[#8E9A86] gh-link-underline inline-flex items-center gap-1">
                      <Layers className="h-3 w-3" /> Complete the set &amp; save 20%
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pricing options */}
        <div className="grid md:grid-cols-2 gap-6 mt-14">
          <div className="border border-[#D9CBA0]/50 rounded-2xl p-8 bg-white" data-testid="pricing-individual">
            <div className="flex items-center gap-2 text-[#3A3632] font-medium"><Heart className="h-5 w-5 text-[#C4A4A4]" /> Individually</div>
            <p className="text-sm text-[#6B655F] mt-2">Collect the cards that speak to your heart, one at a time.</p>
            <div className="font-heading text-4xl text-[#8E9A86] mt-4">{money(cards[0]?.price || 6)}<span className="text-base text-[#8A8175] font-sans"> / card</span></div>
          </div>
          <div className="border-2 border-[#8E9A86] rounded-2xl p-8 bg-[#8E9A86]/8 relative" data-testid="pricing-set">
            <span className="absolute -top-3 left-8 text-[10px] tracking-wide uppercase font-semibold text-white bg-[#8E9A86] rounded-full px-3 py-1">Best value · Save {Math.round(SET_DISCOUNT * 100)}%</span>
            <div className="flex items-center gap-2 text-[#3A3632] font-medium"><Layers className="h-5 w-5 text-[#8E9A86]" /> The Collector's Set</div>
            <p className="text-sm text-[#6B655F] mt-2">All {cards.length || 4} designs in the series, beautifully bundled.</p>
            <div className="font-heading text-4xl text-[#8E9A86] mt-4">{money(setPrice || 19.2)}
              {individualTotal > 0 && <span className="text-base text-[#8A8175] font-sans line-through ml-2">{money(individualTotal)}</span>}
            </div>
            {live && (
              <button onClick={addSet} disabled={setInCart} data-testid="pricing-add-set" className="w-full mt-5 bg-[#8E9A86] text-white rounded-full px-6 py-3 font-medium hover:bg-[#7A8772] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60">
                {setInCart ? <><Check className="h-4 w-4" /> In cart</> : <><Layers className="h-4 w-4" /> Add the Collector's Set</>}
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Notify (teaser only) */}
      {!live && (
        <section className="bg-[#3A3632] py-20" data-testid="hope-cards-notify">
          <div className="max-w-2xl mx-auto px-5 sm:px-8 text-center">
            <BellRing className="h-8 w-8 text-[#D9CBA0] mx-auto" />
            <h2 className="font-heading text-3xl sm:text-4xl text-[#FAF7F2] mt-4">Be the first to collect them.</h2>
            <p className="text-[#D9CBA0]/90 mt-3">Leave your email, and we'll let you know the moment the Hope Cards series launches.</p>
            <form onSubmit={notify} className="mt-8 bg-[#FAF7F2] rounded-2xl p-6 text-left" data-testid="notify-form">
              <label className="block"><span className="text-sm text-[#3A3632] font-medium mb-1.5 block">Email</span>
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="notify-email"
                  className="w-full border border-[#D1CCC5] rounded-lg px-4 py-3 bg-white outline-none focus:border-[#8E9A86] focus:ring-2 focus:ring-[#8E9A86]/30" placeholder="you@example.com" /></label>
              <label className="block mt-3"><span className="text-sm text-[#3A3632] font-medium mb-1.5 block">Anything you'd love to see? <span className="text-[#8A8175] font-normal">(optional)</span></span>
                <textarea rows={3} value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="notify-message"
                  className="w-full border border-[#D1CCC5] rounded-lg px-4 py-3 bg-white outline-none focus:border-[#8E9A86] focus:ring-2 focus:ring-[#8E9A86]/30 resize-none" placeholder="Bulk orders for a church, a specific message, resale interest…" /></label>
              <button type="submit" disabled={busy} data-testid="notify-submit" className="w-full mt-4 bg-[#8E9A86] text-white rounded-full px-6 py-3.5 font-medium hover:bg-[#7A8772] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60">
                <BellRing className="h-4 w-4" /> {busy ? "Adding you…" : "Notify me at launch"}
              </button>
            </form>
          </div>
        </section>
      )}

      <section className="py-14 text-center">
        <p className="flex items-center justify-center gap-2 text-[#8A8175]"><QrCode className="h-4 w-4 text-[#8E9A86]" /> Every card links to a short welcome video from Hope &amp; Favor.</p>
        <Link to="/shop" data-testid="hope-cards-shop" className="btn-outline mt-6 inline-flex">Shop the Care Boxes</Link>
        <div className="mt-8 flex flex-col items-center gap-2">
          <span className="gh-overline text-[10px]">Share the hope</span>
          <ShareButton title="Hope & Favor™ Keepsake Hope Cards" text="Printable, personalized Keepsake Hope Cards from Hope & Favor — thoughtfully curated, beautifully given." />
        </div>
      </section>
    </div>
  );
}
